import React, { createContext, useMemo, useState, useEffect, useCallback } from 'react';
import { Participant, Room } from 'twilio-video';

interface SelectedParticipantContextProps {
  selectedParticipant: Participant | null;
  updateSelectedParticipant: React.Dispatch<Participant>;
}

export const SelectedParticipantContext = createContext<SelectedParticipantContextProps>(null!);

interface SelectedParticipantProviderProps {
  room: Room | null;
}

const SelectedParticipantProvider: React.FC<SelectedParticipantProviderProps> = ({ room, children }) => {
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);

  const updateSelectedParticipant = useCallback(
    (participant: Participant) =>
      setSelectedParticipant((prevParticipant) => (prevParticipant === participant ? null : participant)),
    [],
  );

  useEffect(() => {
    if (room) {
      const handleDisconnect = () => setSelectedParticipant(null);
      const handleParticipantDisconnected = (participant: Participant) =>
        setSelectedParticipant((prevParticipant) => (prevParticipant === participant ? null : prevParticipant));

      room.on('disconnected', handleDisconnect);
      room.on('participantDisconnected', handleParticipantDisconnected);

      return () => {
        room.off('disconnected', handleDisconnect);
        room.off('participantDisconnected', handleParticipantDisconnected);
      };
    }
  }, [room]);

  const contextValue = useMemo(
    () => ({
      selectedParticipant,
      updateSelectedParticipant,
    }),
    [selectedParticipant, updateSelectedParticipant],
  );

  return <SelectedParticipantContext.Provider value={contextValue}>{children}</SelectedParticipantContext.Provider>;
};

export default SelectedParticipantProvider;
